import { Button, Input, Modal, Tooltip } from 'antd';
import { DislikeFilled, DislikeOutlined, LikeFilled, LikeOutlined } from '@ant-design/icons';
import { memo } from 'react';
import { useFeedback, FeedbackState } from './useFeedback';
import styles from '../Chat.module.scss';

interface Props {
  tenantId: string;
  threadId: string;
  messageId: string;
}

function positiveTooltip(state: FeedbackState): string {
  if (state === 'positive') return 'Marked as helpful';
  if (state === 'negative') return 'Feedback submitted';
  return 'Helpful';
}

function negativeTooltip(state: FeedbackState): string {
  if (state === 'negative') return 'Marked as not helpful';
  if (state === 'positive') return 'Feedback submitted';
  return 'Not helpful';
}

const MessageFeedback = ({ tenantId, threadId, messageId }: Props) => {
  const {
    feedbackState,
    modalOpen,
    comment,
    setComment,
    submitting,
    submitPositive,
    openNegativeModal,
    submitNegative,
    cancelNegativeModal,
  } = useFeedback(tenantId, threadId, messageId);

  const submitted = feedbackState !== 'none';

  return (
    <>
      <Tooltip title={positiveTooltip(feedbackState)} placement="topLeft">
        <Button
          size="small"
          type="text"
          shape="circle"
          aria-label="Mark as helpful"
          icon={feedbackState === 'positive' ? <LikeFilled style={{ color: '#52c41a' }} /> : <LikeOutlined />}
          className={styles.messageActionButton}
          onClick={submitPositive}
          disabled={submitted}
        />
      </Tooltip>
      <Tooltip title={negativeTooltip(feedbackState)} placement="topLeft">
        <Button
          size="small"
          type="text"
          shape="circle"
          aria-label="Mark as not helpful"
          icon={feedbackState === 'negative' ? <DislikeFilled style={{ color: '#ff4d4f' }} /> : <DislikeOutlined />}
          className={styles.messageActionButton}
          onClick={openNegativeModal}
          disabled={submitted}
        />
      </Tooltip>
      <Modal
        title="Share your feedback"
        open={modalOpen}
        onCancel={cancelNegativeModal}
        onOk={submitNegative}
        okText="Submit"
        cancelText="Cancel"
        confirmLoading={submitting}
        destroyOnHidden
      >
        <Input.TextArea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Tell us what went wrong "
          maxLength={1000}
          rows={4}
          showCount
          autoFocus
        />
      </Modal>
    </>
  );
};

export default memo(MessageFeedback);
